import { FC, useEffect, useState } from "react";
import {
  Text,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Select,
} from "@radix-ui/themes";
import { useTenantZoneChallenges } from "@/features/challenge/hooks/useTenantZoneChallenges";
import { Zones } from "@/models/database/zones/Zones.model";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { Spinner } from "@radix-ui/themes";
import { Leaderboard } from "@/features/challenge/components/leaderboard/Leaderboard";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "@/routes/siteRoutesList";

interface SingleZoneStandingsProps {
  zone: Zones;
}

export const SingleZoneStandings: FC<SingleZoneStandingsProps> = ({
  zone,
}): JSX.Element => {
  const navigate = useNavigate();
  const { data, loading, error } = useTenantZoneChallenges(
    brand.tenantId,
    zone.slug
  );
  const [selectedLeaderboard, setSelectedLeaderboard] = useState<string | null>(
    null
  );

  useEffect(() => {
    // Set the default selected leaderboard to the first item in the data array
    if (data && data.length > 0) {
      setSelectedLeaderboard(data[0].slug);
    }
  }, [data]);

  const handleChangeLeaderboard = (value: string) => {
    setSelectedLeaderboard(value);
  };

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <Text>Error: {error.message}</Text>;
  }

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <Container px={{ initial: "4", md: "2", xl: "0" }}>
      <Flex
        justify={{ initial: "start", lg: "between" }}
        direction={{ initial: "column", lg: "row" }}
        align={{ initial: "start", lg: "end" }}
        gap={{ initial: "3", lg: "0" }}
        mb="3"
      >
        <Box>
          <Heading as="h2" mb="0">
            Leaderboard
          </Heading>
          <Text as="p" size="2" color="gray">
            Current standings in Logitech's monthly challenges.
          </Text>
        </Box>
        <Select.Root
          defaultValue={data[0].slug}
          onValueChange={handleChangeLeaderboard}
        >
          <Select.Trigger />
          <Select.Content>
            <Select.Group>
              <Select.Label>Challenges</Select.Label>
              {data.map((item, index) => (
                <Select.Item key={index} value={item.slug}>
                  {item.game} - {item.name}
                </Select.Item>
              ))}
            </Select.Group>
          </Select.Content>
        </Select.Root>
      </Flex>
      <Flex direction="column" gap="3">
        <Box>
          <Flex direction="column" gap="3">
            {selectedLeaderboard && (
              <>
                <Leaderboard
                  slug={encodeURIComponent(selectedLeaderboard)} //required to be encoded!
                  isPreview={false}
                  status={
                    data.find((item) => item.slug === selectedLeaderboard)
                      ?.status
                  }
                  client={
                    data.find((item) => item.slug === selectedLeaderboard)
                      ?.client
                  }
                  game={
                    data.find((item) => item.slug === selectedLeaderboard)?.game
                  }
                  maxItems={5}
                />

                <Flex justify="center" mt="4">
                  <Button
                    onClick={() =>
                      navigate(
                        `${siteRoutes["zone"]}${zone.slug}${siteRoutes["challengesAndSlug"]}${selectedLeaderboard}`
                      )
                    }
                  >
                    View more
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </Box>
      </Flex>
    </Container>
  );
};
